import type { RawNote } from './quantize.js'

/**
 * MIDI note events to raw notes.
 *
 * Deliberately NOT typed against `webmidi`: this is `music-lib`, which never imports a browser
 * library, and the four fields below are the whole of what the capture needs. The hook that owns
 * the device maps `MidiNoteEvent` onto this shape.
 *
 * Sustain pedal is ignored in v1 (spec §5): a pedalled note's real length is a question about the
 * instrument's release, not about when the player struck the key, and the melody stores the latter.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §5
 */

export interface RawMidiEvent {
  type: 'noteon' | 'noteoff'
  midi: number
  /** Attack velocity, 0-1. Zero on a noteoff, which never reads it. */
  velocity: number
  /** The event's timestamp on whatever clock the caller used for `t0Ms`. */
  timeMs: number
}

interface OpenNote {
  midi: number
  onsetMs: number
  velocity: number
}

/**
 * @param events every note event of the take, in any order
 * @param t0Ms the click track's beat 1, on the same clock as `event.timeMs`
 * @param endMs the take's last moment; notes still held there are closed at it. Without it, a note
 *   that was never released is dropped - there is no honest length to give it.
 */
export const midiEventsToRawNotes = (events: RawMidiEvent[], t0Ms: number, endMs?: number): RawNote[] => {
  /** Stable sort: a noteoff sharing a timestamp with a noteon must still close the earlier note. */
  const ordered = [...events].sort((a, b) => a.timeMs - b.timeMs)

  const notes: RawNote[] = []
  /** One open note per pitch. A retrigger closes the open one, which is what a repeated key is. */
  const open = new Map<number, OpenNote>()

  const close = (note: OpenNote, atMs: number) => {
    notes.push({
      midi: note.midi,
      onsetMs: note.onsetMs - t0Ms,
      durationMs: atMs - note.onsetMs,
      velocity: note.velocity,
    })
  }

  for (const event of ordered) {
    const held = open.get(event.midi)
    if (event.type === 'noteon') {
      if (held) {
        close(held, event.timeMs)
      }
      open.set(event.midi, { midi: event.midi, onsetMs: event.timeMs, velocity: event.velocity })
      continue
    }
    /** A noteoff with nothing open is a device echo, or a take that began mid-note. Ignore it. */
    if (held) {
      close(held, event.timeMs)
      open.delete(event.midi)
    }
  }

  if (endMs !== undefined) {
    for (const held of open.values()) {
      close(held, endMs)
    }
  }

  return notes.sort((a, b) => a.onsetMs - b.onsetMs)
}
